import React from 'react';
import { Alert, App, Input, Space, Typography } from 'antd';
import type { TFunction } from 'i18next';
import { formatUnit } from '../../utils/units';
import { formatDateEuropean } from '../../utils/dateUtils';
import { formatLocalizedQuantity } from '../../utils/numberFormatting';
import { CARGO_QUANTITY_TOLERANCE } from '../../utils/transportCargoAllocation';

type ModalApi = ReturnType<typeof App.useApp>['modal'];

interface OperationalWarning {
  code: string;
  details?: Record<string, any>;
}

function readConfirmation(error: any) {
  const data = error?.response?.data;
  if (data?.code !== 'OPERATIONAL_EXCEPTION_CONFIRMATION_REQUIRED') return null;
  if (!data.confirmationFingerprint || !Array.isArray(data.warnings)) return null;
  return data as {
    confirmationFingerprint: string;
    warnings: OperationalWarning[];
    reasonMaxLength?: number;
  };
}

function warningText(warning: OperationalWarning, t: TFunction) {
  const details = warning.details || {};
  if (warning.code === 'WZ_MISSING_PURCHASE_INVOICE' || warning.code === 'WZ_BEFORE_PURCHASE_INVOICE' || warning.code === 'WZ_PENDING_STOCK_ISSUE') {
    return String(t('common:operationalException.warnings.wzBeforePurchaseInvoice', { ...details, unit: formatUnit(details.unit, 'pl') }));
  }
  if (warning.code === 'PZ_QUANTITY_RECEIVED_EXCEEDS_ORDERED') {
    return String(t('common:operationalException.warnings.pzOverReceipt', {
      ...details,
      unit: formatUnit(details.unit, 'pl'),
    }));
  }
  if (warning.code === 'PZ_INVOICE_DATE_AFTER_RECEIPT') {
    return String(t('common:operationalException.warnings.pzInvoiceDateAfterReceipt', details));
  }
  if (warning.code === 'PZ_INVOICE_NET_AMOUNT_MISMATCH') {
    return String(t('common:operationalException.warnings.pzInvoiceNetAmountMismatch', details));
  }
  if (warning.code === 'CARGO_OVER_ALLOCATION') {
    const rows = Array.isArray(details.overflow) ? details.overflow : [];
    return rows.map((row: Record<string, unknown>) => {
      // `allowed` is intentionally the cumulative guard ceiling: it includes
      // the 0.5-unit tolerance so repeated transports cannot each consume a
      // fresh tolerance. It must never be labelled as available stock.
      const ordered = Number(row.ordered) || 0;
      const allocatedElsewhere = Number(row.allocatedElsewhere) || 0;
      const available = Math.max(ordered - allocatedElsewhere, 0);
      const formatQuantity = (value: number) => formatLocalizedQuantity(value, 'pl');
      return String(t('common:operationalException.warnings.cargoOverAllocation', {
        ...row,
        attempted: formatQuantity(Number(row.attempted) || 0),
        ordered: formatQuantity(ordered),
        allocatedElsewhere: formatQuantity(allocatedElsewhere),
        available: formatQuantity(available),
        tolerance: formatQuantity(CARGO_QUANTITY_TOLERANCE),
      }));
    }).join('\n');
  }
  if (warning.code === 'TRANSPORT_DELIVERY_EXCEEDS_PLANNED') {
    return String(t('common:operationalException.warnings.transportDeliveryExceedsPlanned', {
      ...details,
      unit: formatUnit(details.unit, 'pl'),
      plannedQuantity: formatLocalizedQuantity(Number(details.plannedQuantity) || 0, 'pl'),
      deliveredQuantity: formatLocalizedQuantity(Number(details.deliveredQuantity) || 0, 'pl'),
      difference: formatLocalizedQuantity(Number(details.difference) || 0, 'pl'),
    }));
  }
  if (warning.code === 'TRANSPORT_ACTUAL_DELIVERY_BEFORE_LOADING') {
    return String(t('common:operationalException.warnings.transportDeliveryBeforeLoading', {
      actualDelivery: formatDateEuropean(details.actualDelivery),
      loadingDate: formatDateEuropean(details.loadingDate),
    }));
  }
  return warning.code;
}

async function askForReason(
  modal: ModalApi,
  warnings: OperationalWarning[],
  maxLength: number,
  t: TFunction,
): Promise<string | null> {
  let reason = '';
  return new Promise((resolve) => {
    modal.confirm({
      title: t('common:operationalException.title'),
      width: 620,
      okText: t('common:operationalException.continue'),
      cancelText: t('common:cancel'),
      content: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message={t('common:operationalException.description')}
          />
          {warnings.map((warning, index) => (
            <Typography.Paragraph key={`${warning.code}-${index}`} style={{ whiteSpace: 'pre-line', marginBottom: 0 }}>
              {warningText(warning, t)}
            </Typography.Paragraph>
          ))}
          <div>
            <Typography.Text strong>{t('common:operationalException.reasonLabel')}</Typography.Text>
            <Input.TextArea
              rows={3}
              maxLength={maxLength}
              showCount
              placeholder={t('common:operationalException.reasonPlaceholder')}
              onChange={(event) => { reason = event.target.value; }}
            />
          </div>
        </Space>
      ),
      onOk: () => {
        if (!reason.trim()) return Promise.reject(new Error(t('common:operationalException.reasonRequired')));
        resolve(reason.trim());
        return Promise.resolve();
      },
      onCancel: () => resolve(null),
    });
  });
}

export async function submitWithOperationalException<T>(
  request: (payload: Record<string, unknown>) => Promise<T>,
  payload: Record<string, unknown>,
  modal: ModalApi,
  t: TFunction,
): Promise<T | null> {
  let nextPayload = payload;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return await request(nextPayload);
    } catch (error) {
      const confirmation = readConfirmation(error);
      if (!confirmation) throw error;
      const reason = await askForReason(
        modal,
        confirmation.warnings,
        confirmation.reasonMaxLength || 500,
        t,
      );
      if (!reason) return null;
      nextPayload = {
        ...payload,
        confirmOperationalException: true,
        operationalExceptionFingerprint: confirmation.confirmationFingerprint,
        operationalExceptionReason: reason,
      };
    }
  }
  throw new Error('Operational exception confirmation changed repeatedly');
}
